import { UserService } from '../../users';
import UserModel from '../../../../view/pages/posm/models/User.model';

export default {
  namespaced: true,
  state: {
    users: [],
    storeUsers: [],
    currentUser: null,
    userProfile: null,
    paginations: {
      total: 0,
      totalPages: 0,
      currentPage: 1,
    },
    loading: false,
    error: null,
    searchResults: [],
  },
  getters: {
    getUsers: (state) => state.users,
    getStoreUsers: (state) => state.storeUsers,
    getCurrentUser: (state) => state.currentUser,
    getUserProfile: (state) => state.userProfile,
    getPaginations: (state) => state.paginations,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getSearchResults: (state) => state.searchResults,
  },
  mutations: {
    SET_USERS(state, { data, paginations }) {
      state.users = data;
      state.paginations = paginations;
    },
    SET_STORE_USERS(state, users) {
      state.storeUsers = users;
    },
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;
    },
    SET_USER_PROFILE(state, profile) {
      state.userProfile = profile;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },
  },
  actions: {
    async fetchUsersByStore({ commit }, storeId) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.getUsersByStore(storeId);
        commit('SET_STORE_USERS', data.data);
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchUsersByListStore({ commit }, storeIds) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.getUsersByListStore(storeIds);
        commit('SET_STORE_USERS', data.data);
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createUser({ commit }, userData) {
      commit('SET_LOADING', true);
      try {
        const response = await UserService.createUser(userData);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateUser({ commit }, userData) {
      commit('SET_LOADING', true);
      try {
        const response = await UserService.updateUser(userData);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchUserProfile({ commit }) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.getProfile();
        commit('SET_USER_PROFILE', data);
        return data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async findUserByUsername({ commit }, userName) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.findByUsername(userName);
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async listUsers({ commit }, params) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.listUsers(params);
        commit('SET_USERS', {
          data: data.data.data,
          paginations: data.data.paginations,
        });
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async generatePassword({ commit }, password) {
      try {
        const { data } = await UserService.generatePassword(password);
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      }
    },

    async checkUsername({ commit }, userName) {
      try {
        const { data } = await UserService.checkUsername(userName);
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      }
    },

    async fetchUserById({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.findById(id);
        commit('SET_CURRENT_USER', data.data);
        return data.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async activateUser({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const response = await UserService.activateUser(id);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async restorePassword({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const response = await UserService.restorePassword(id);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Smart search for users
     * @param {Object} context - Vuex context
     * @param {Object} params - Search parameters
     * @param {string|undefined} params.keyword - Keyword to search
     * @param {string|undefined} params.storeId - StoreId to search
     * @returns {Promise} - API response
     */
    async smartSearch({ commit }, params) {
      commit('SET_LOADING', true);
      try {
        const { data } = await UserService.smartSearch(params);
        const results = UserModel.fromJsonArray(data?.data.data);
        commit('SET_SEARCH_RESULTS', results);
        return { data: results, paginations: data?.data.paginations };
      } catch (error) {
        commit('SET_ERROR', error);
        return {
          data: [],
          paginations: {
            total: 0,
            totalPages: 0,
            currentPage: 1,
          },
        };
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },
};
